import { useEffect, useMemo, useState } from "react"
import Input from "../../../../components/Input"
import { StepProps } from "../../../../components/Stepper"
import { AuthData } from "../../../../types/steps/auth"
import Calendar from "../../../../components/Calendar"
import { useRequest } from "../../../../hooks/useRequest"
import { userStore } from "../../../../store/userStore"
import { applyPendingLocation, getAuthCityId } from "../../../../lib/locationSwitch"
import { useNavigate } from "react-router-dom"
import AuthLegalLink from "../../../../features/auth/AuthLegalLink"
import AuthSheetLayout, { AuthSheetButton } from "../../../../features/auth/AuthSheetLayout"
import { authCheckboxClass, authLabelClass, authLabelStyle, authLinkClass } from "../../../../features/auth/authTheme"
import { clearAuthDraft, loadAuthDraft, patchAuthDraft } from "../../../../lib/authDraft"

const emptyForm = {
    lastname: "",
    firstname: "",
    patronymic: "",
    birthday: "",
    email: "",
}

const emptyChecked = {
    personalData: false,
    privacy: false,
    terms: false,
}

export default function User({ params, goBack }: StepProps<AuthData>) {
    const { phone, phoneVerification: phone_verification } = params
    const { request } = useRequest()
    const { setUser } = userStore()
    const router = useNavigate()
    const userDraft = loadAuthDraft()?.userForm
    const [form, setForm] = useState(userDraft?.form ?? emptyForm)
    const [checked, setChecked] = useState(userDraft?.checked ?? emptyChecked)
    const [caledarOpen, setCalendarOpen] = useState(false)

    useEffect(() => {
        patchAuthDraft({ userForm: { form, checked } })
    }, [form, checked])

    const disabled = useMemo(() => {
        const requiredFieldsFilled = Boolean(
            form.lastname.trim() && form.firstname.trim() && form.birthday && form.email.trim(),
        )
        const allChecked = checked.personalData && checked.privacy && checked.terms
        return !(requiredFieldsFilled && allChecked)
    }, [form, checked])

    const saveUser = async () => {
        const user = await request("POST", "registration", {
            phone: `8${phone}`,
            full_name: `${form.lastname} ${form.firstname} ${form.patronymic}`.trim(),
            birthday: form.birthday,
            email: form.email,
            city_id: getAuthCityId(),
            gender: 1,
            phone_verification: phone_verification,
        })

        setUser(user)
        clearAuthDraft()
        applyPendingLocation()
        router(-1)
    }

    return (
        <AuthSheetLayout
            title="Регистрация"
            onBack={goBack}
            footer={
                <AuthSheetButton variant="primary" disabled={disabled} onClick={saveUser}>
                    Сохранить
                </AuthSheetButton>
            }>
            <Input
                value={form.lastname}
                required
                label="Фамилия"
                placeholder="Введите фамилию"
                onChange={value => setForm({ ...form, lastname: value.replace(/[^а-яё\-]/gi, "") })}
            />
            <Input
                value={form.firstname}
                required
                label="Имя"
                placeholder="Введите имя"
                onChange={value => setForm({ ...form, firstname: value })}
            />
            <Input
                value={form.patronymic}
                label="Отчество"
                placeholder="Введите отчество"
                onChange={value => setForm({ ...form, patronymic: value })}
            />
            <Input
                value={form.birthday ? form.birthday.replaceAll("-", ".") : ""}
                label="Дата рождения"
                placeholder="Выберите дату рождения"
                onChange={value => setForm({ ...form, birthday: value })}
                onFocus={() => setCalendarOpen(true)}
            />
            <Input
                value={form.email}
                required
                label="Email"
                placeholder="Введите Email"
                onChange={value => setForm({ ...form, email: value })}
            />
            <Calendar
                open={caledarOpen}
                onClose={() => setCalendarOpen(false)}
                value={form.birthday}
                onSelect={value => setForm({ ...form, birthday: value })}
            />

            <div className="flex flex-col gap-3">
                <div className="flex items-start gap-3">
                    <input
                        type="checkbox"
                        checked={checked.personalData}
                        onChange={e => setChecked({ ...checked, personalData: e.target.checked })}
                        className={authCheckboxClass}
                        id="consent-personal-data"
                    />
                    <label htmlFor="consent-personal-data" className={authLabelClass} style={authLabelStyle}>
                        Я согласен с{" "}
                        <AuthLegalLink to="/personal-data" className={authLinkClass}>
                            положением об обработке персональных данных (152-ФЗ)
                        </AuthLegalLink>
                    </label>
                </div>
                <div className="flex items-start gap-3">
                    <input
                        type="checkbox"
                        checked={checked.privacy}
                        onChange={e => setChecked({ ...checked, privacy: e.target.checked })}
                        className={authCheckboxClass}
                        id="consent-privacy"
                    />
                    <label htmlFor="consent-privacy" className={authLabelClass} style={authLabelStyle}>
                        Я согласен с{" "}
                        <AuthLegalLink to="/privacy" className={authLinkClass}>
                            политикой конфиденциальности
                        </AuthLegalLink>
                    </label>
                </div>
                <div className="flex items-start gap-3">
                    <input
                        type="checkbox"
                        checked={checked.terms}
                        onChange={e => setChecked({ ...checked, terms: e.target.checked })}
                        className={authCheckboxClass}
                        id="consent-terms"
                    />
                    <label htmlFor="consent-terms" className={authLabelClass} style={authLabelStyle}>
                        Я согласен с{" "}
                        <AuthLegalLink to="/terms" className={authLinkClass}>
                            пользовательским соглашением
                        </AuthLegalLink>
                    </label>
                </div>
            </div>
        </AuthSheetLayout>
    )
}
